import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';

import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { CommonInformationComponent } from '@connect-services/client/components/modals/data-export-modal/common-information/common-information.component';
import { EntitySelectionComponent } from '@connect-services/client/components/modals/data-export-modal/entity-selection/entity-selection.component';
import { GeographicZoneComponent } from '@connect-services/client/components/modals/data-export-modal/geographic-zone/geographic-zone.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { DataExportService } from '@connect-services/client/services/data-export.service';
import { SelectionStateService } from '@connect-services/client/services/map/selection/selection-state.service';
import {
    TextToggleComponent,
    ToggleOption,
} from '@connect-services/src/lib/components/text-toggle/text-toggle.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

export enum ExportSelectionModeEnum {
    ENTITY_SELECTION = 'entity_selection',
    GEOGRAPHIC_ZONE = 'geographic_zone',
}

export interface DataExportModalData {
    exportSelectionMode?: ExportSelectionModeEnum;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonInformationComponent,
        ComplexModalTemplate,
        EntitySelectionComponent,
        GeographicZoneComponent,
        RessourcesModule,
        TextToggleComponent,
    ],
    selector: 'app-data-export-modal',
    templateUrl: './data-export-modal.component.html',
})
export class DataExportModalComponent implements OnInit {
    protected readonly confirmTooltip = computed<string>(() => {
        if (this.dataExportService.canConfirm()) {
            return '';
        }

        return this.translocoService.translate('selectAtLeastOneLayer');
    });
    protected readonly selectionModeOptions = computed<ToggleOption<ExportSelectionModeEnum>[]>(() => {
        const hasEntitySelection: boolean = this.selectionStateService.hasSelectedEntities();
        const isEntitySelectionDisabled =
            this.dataExportService.selectionMode() !== ExportSelectionModeEnum.ENTITY_SELECTION && !hasEntitySelection;
        const isGeographicZoneDisabled = this.contentLayoutService.isDataTableView() || hasEntitySelection;

        return this.getSelectionModeOptions(isEntitySelectionDisabled, isGeographicZoneDisabled);
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly dataExportService = inject(DataExportService);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly contentLayoutService = inject(ContentLayoutService);
    private dialogRef = inject(MatDialogRef<DataExportModalComponent>);
    private readonly selectionStateService = inject(SelectionStateService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        const initialExportSelectionMode: ExportSelectionModeEnum | undefined =
            inject<DataExportModalData>(MAT_DIALOG_DATA).exportSelectionMode;

        if (initialExportSelectionMode) {
            this.dataExportService.selectionMode.set(initialExportSelectionMode);
        }

        effect(() => {
            if (
                this.dataExportService.selectionMode() !== ExportSelectionModeEnum.GEOGRAPHIC_ZONE ||
                !this.selectionStateService.hasSelectedEntities()
            ) {
                return;
            }

            this.dataExportService.selectionMode.set(ExportSelectionModeEnum.ENTITY_SELECTION);
        });
    }

    public closeModal(modalResultEnum: ModalResultEnum): void {
        this.dialogRef.close({ type: modalResultEnum });
    }

    public async ngOnInit(): Promise<void> {
        await this.dataExportService.refreshMapFilteredState();
    }

    protected onExportModeChange(selection: ExportSelectionModeEnum[]): void {
        const nextMode = selection[0] ?? null;

        if (nextMode === ExportSelectionModeEnum.GEOGRAPHIC_ZONE && this.selectionStateService.hasSelectedEntities()) {
            return;
        }

        this.dataExportService.selectionMode.set(nextMode);
    }

    private getSelectionModeOptions(
        isEntitySelectionDisabled: boolean,
        isGeographicZoneDisabled: boolean,
    ): ToggleOption<ExportSelectionModeEnum>[] {
        return [
            {
                isDisable: isEntitySelectionDisabled,
                title: this.translocoService.translate('entitySelection'),
                tooltip: isEntitySelectionDisabled ? 'noSelectionInProgress' : undefined,
                value: ExportSelectionModeEnum.ENTITY_SELECTION,
            },
            {
                isDisable: isGeographicZoneDisabled,
                title: this.translocoService.translate('geographicZone'),
                tooltip: isGeographicZoneDisabled ? 'exportByGeographicZoneUnavailableWhenEntitiesSelected' : undefined,
                value: ExportSelectionModeEnum.GEOGRAPHIC_ZONE,
            },
        ];
    }
}
